import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { unionBy } from 'lodash';
import { ChatRooms, ChatRoomsData } from './types';
import { fetchBroadcastChatRoom, fetchChatRooms, fetchChatRoomsBySpecialistID } from './thunk';
import { UpdateAvatarMessage, UpdateNameMessage, ViewedMessage } from '../../pages/types';
import { ChatMessageData } from '../chatMessages/types';

const initialState: ChatRooms = {
  entities: [],
  limit: 30,
  offset: 0,
  isInfiniteDisabled: false,
  loading: 'idle',
  isFirstLoading: true,
};

const lastMessageNormalize = (message: ChatMessageData) => {
  switch (message.messageType) {
    case 'file':
      return { ...message, message: `📎 ${message.fileInfo?.originalName}` };
    case 'image':
      return { ...message, message: `📷 ${message.fileInfo?.originalName}` };
    case 'video':
      return { ...message, message: `📷 ${message.fileInfo?.originalName}` };

    default:
      return { ...message };
  }
};

export const chatRoomsSlice = createSlice({
  name: 'chatRooms',
  initialState,
  reducers: {
    createRoom: (state, action: PayloadAction<ChatRoomsData>) => {
      const room = state.entities.find((item) => item.room.id === action.payload.room.id);
      if (room) {
        room.avatar = action.payload.avatar;
        room.name = action.payload.name;
        room.last_message = lastMessageNormalize(action.payload.last_message);
        room.room = action.payload.room;
      } else {
        const setData = {
          ...action.payload,
          last_message: lastMessageNormalize(action.payload.last_message),
        };
        state.entities.push(setData);
      }
    },
    resetOffset: (state) => {
      state.offset = 0;
      state.isInfiniteDisabled = false;
    },
    updateAvatar: (state, action: PayloadAction<UpdateAvatarMessage>) => {
      state.entities.forEach((item) => {
        if (item.room.client_id === action.payload.fromId) {
          item.avatar = action.payload.avatar;
        }
      });
    },
    updateName: (state, action: PayloadAction<UpdateNameMessage>) => {
      state.entities.forEach((item) => {
        if (item.room.client_id === action.payload.fromId) {
          item.name = action.payload.name;
        }
      });
    },
    setLastMessageViewed: (state, action: PayloadAction<ViewedMessage>) => {
      state.entities.forEach((item) => {
        if (item.room.id === action.payload.room_id) {
          item.last_message.status = 'viewed';
        }
      });
    },
    resetRooms: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchChatRooms.fulfilled, (state, action: PayloadAction<ChatRoomsData[]>) => {
      state.isFirstLoading = false;

      if (action.payload.length === 0) {
        state.isInfiniteDisabled = true;
        return;
      }

      action.payload.forEach((item) => {
        const editedRoom = state.entities.find((i) => i.room.id === item.room.id);
        if (editedRoom) {
          editedRoom.last_message = lastMessageNormalize(item.last_message);
          editedRoom.room = item.room;
          editedRoom.avatar = item.avatar;
          editedRoom.is_blocked_chat = item.is_blocked_chat;
        } else {
          state.entities.push({
            ...item,
            last_message: lastMessageNormalize(item.last_message),
          });
        }
      });

      state.offset = state.offset + state.limit;
    });
    builder.addCase(fetchChatRoomsBySpecialistID.fulfilled, (state, action: PayloadAction<ChatRoomsData[]>) => {
      if (action.payload.length) {
        state.entities = unionBy(state.entities, action.payload, 'id');
      }
    });
    builder.addCase(fetchBroadcastChatRoom.fulfilled, (state, action: PayloadAction<ChatRoomsData>) => {
      if (!action?.payload) return;
      const newRoom = {
        ...action.payload,
        last_message: lastMessageNormalize(action.payload.last_message),
      };

      state.entities = state.entities.filter((room) => room.room.id !== newRoom.room.id);

      state.entities.unshift(newRoom);
    });
  },
});

export default chatRoomsSlice.reducer;
export const { createRoom, updateAvatar, updateName, resetOffset, setLastMessageViewed, resetRooms } =
  chatRoomsSlice.actions;
